(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    api.init(root, root.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var STANDARD_EVENTS = [
    'app_installed',
    'app_opened',
    'appointment_scheduled',
    'checkout_started',
    'contents_viewed',
    'items_added',
    'lead_created',
    'order_created',
    'page_viewed',
    'registration_completed',
    'subscription_created',
    'trial_started'
  ];

  function consentAllowed(value) {
    return Boolean(value && value.analytics === true && value.marketing === true);
  }

  function validCustomEventName(name) {
    return typeof name === 'string' &&
      /^[a-z0-9](?:[a-z0-9_-]{0,62}[a-z0-9])?$/.test(name) &&
      STANDARD_EVENTS.indexOf(name) === -1 &&
      name !== 'custom';
  }

  function classifyPage(context, documentRef) {
    var pageType = String(context.pageType || '').toLowerCase();
    var marker = String(
      (context.templateSuffix || '') + ' ' + (context.pageTitle || '')
    ).toLowerCase();
    var explicitSale = Boolean(
      documentRef &&
      documentRef.querySelector &&
      documentRef.querySelector('[data-oai-page-kind="sale"]')
    );

    if (pageType === 'product') return 'product';
    if (pageType === 'collection') return 'collection';

    if (
      pageType === 'page' &&
      (explicitSale || /(sale|black.?friday|cyber|promotion|promo)/.test(marker))
    ) {
      return 'sale_page';
    }

    if (
      pageType === 'page' &&
      /(^|[\s.-])(pf-|landing|pre-owned|718|981|987|991|992|996|997|porsche)/.test(marker)
    ) {
      return 'landing_page';
    }

    return pageType === 'page' ? 'page' : (pageType || 'page');
  }

  function classifyAction(action) {
    if (!action || action.ignored) return null;

    if (action.explicitEvent) {
      return validCustomEventName(action.explicitEvent)
        ? action.explicitEvent
        : null;
    }

    if (action.isCalendly) return 'appointment_cta_clicked';
    if (action.href && action.inNavigation) return 'menu_clicked';
    if (action.isCheckout) return 'checkout_started';

    if (
      action.href &&
      action.inMainContent &&
      action.pageKind === 'sale_page'
    ) {
      return 'sale_cta_clicked';
    }

    if (action.href && action.inMainContent && action.isPage) {
      return 'landing_cta_clicked';
    }

    return null;
  }

  function customMeasureArguments(name, content) {
    var data = { type: 'custom' };
    var safeContent = sanitizeContent(content);

    if (
      safeContent &&
      (safeContent.id || safeContent.name || safeContent.content_type)
    ) {
      data.contents = [safeContent];
    }

    return [
      'measure',
      'custom',
      data,
      { custom_event_name: name }
    ];
  }

  function cleanString(value, maxLength) {
    if (value === null || value === undefined) return '';

    return String(value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength || 256);
  }

  function isInteger(value) {
    return typeof value === 'number' &&
      isFinite(value) &&
      Math.floor(value) === value;
  }

  function sanitizeContent(content) {
    if (!content || typeof content !== 'object') return null;

    var safe = {};
    var id = cleanString(content.id, 256);
    var name = cleanString(content.name, 256);
    var contentType = cleanString(content.content_type, 128);
    var currency = cleanString(content.currency, 3).toUpperCase();

    if (id) safe.id = id;
    if (name) safe.name = name;
    if (contentType) safe.content_type = contentType;
    if (isInteger(content.quantity) && content.quantity > 0) {
      safe.quantity = content.quantity;
    }
    if (isInteger(content.amount) && content.amount >= 0) {
      safe.amount = content.amount;
      if (currency) safe.currency = currency;
    }

    return safe;
  }

  function klaviyoEvent(detail) {
    if (!detail || detail.type !== 'submit') return null;

    return String(detail.formId || '').toLowerCase() === 'qpaffv'
      ? 'registration_completed'
      : 'lead_created';
  }

  function cartAddData(response, currency) {
    var rawItems = response && Array.isArray(response.items)
      ? response.items
      : (response ? [response] : []);
    var safeCurrency = cleanString(currency, 3).toUpperCase();
    var contents = [];
    var total = 0;
    var hasAllPrices = rawItems.length > 0;

    rawItems.forEach(function (item) {
      if (!item || typeof item !== 'object') return;

      var quantity = isInteger(item.quantity) && item.quantity > 0
        ? item.quantity
        : 1;
      var unitAmount = null;

      ['final_price', 'discounted_price', 'price'].some(function (key) {
        if (isInteger(item[key]) && item[key] >= 0) {
          unitAmount = item[key];
          return true;
        }
        return false;
      });

      var content = sanitizeContent({
        id: item.variant_id !== undefined ? item.variant_id : item.id,
        name: item.product_title || item.title,
        content_type: 'product',
        quantity: quantity,
        amount: unitAmount,
        currency: safeCurrency
      });

      if (content) contents.push(content);

      if (unitAmount === null) {
        hasAllPrices = false;
      } else {
        total += unitAmount * quantity;
      }
    });

    if (!contents.length) return null;

    var data = {
      type: 'contents',
      contents: contents
    };

    if (hasAllPrices && safeCurrency) {
      data.amount = total;
      data.currency = safeCurrency;
    }

    return data;
  }

  function pageViewData(context) {
    return {
      type: 'contents',
      contents: [sanitizeContent({
        id: context.path || context.pageId,
        name: context.pageTitle,
        content_type: 'page'
      })]
    };
  }

  function contentViewData(context, kind) {
    var resource = context.resource || {};

    return {
      type: 'contents',
      contents: [sanitizeContent({
        id: resource.id || context.path || context.pageId,
        name: resource.name || context.pageTitle,
        content_type: kind,
        amount: resource.amount,
        currency: resource.currency || context.currency
      })]
    };
  }

  function checkoutData(context) {
    var data = {
      type: 'contents',
      contents: (context.cartItems || [])
        .map(sanitizeContent)
        .filter(Boolean)
    };

    if (
      isInteger(context.cartTotal) &&
      context.cartTotal >= 0 &&
      cleanString(context.currency, 3)
    ) {
      data.amount = context.cartTotal;
      data.currency = cleanString(context.currency, 3).toUpperCase();
    }

    return data;
  }

  function isCalendlyUrl(href, baseUrl) {
    if (!href) return false;

    try {
      var parsed = new URL(href, baseUrl || 'https://store.invalid');
      var hostname = parsed.hostname.toLowerCase();
      return hostname === 'calendly.com' || /\.calendly\.com$/.test(hostname);
    } catch (error) {
      return false;
    }
  }

  function normalizeDestination(href, baseUrl) {
    if (!href) return '';

    try {
      var parsed = new URL(href, baseUrl || 'https://store.invalid');
      var base = new URL(baseUrl || 'https://store.invalid');

      if (parsed.origin === base.origin) {
        return parsed.pathname + parsed.hash;
      }

      return parsed.hostname + parsed.pathname;
    } catch (error) {
      return cleanString(href, 256);
    }
  }

  function closest(element, selector) {
    if (!element || typeof element.closest !== 'function') return null;

    try {
      return element.closest(selector);
    } catch (error) {
      return null;
    }
  }

  function attribute(element, name) {
    if (!element || typeof element.getAttribute !== 'function') return '';
    return element.getAttribute(name) || '';
  }

  function describeAction(element, context, documentRef) {
    var explicitOwner = closest(element, '[data-oai-event]');
    var link = closest(element, 'a[href]');
    var source = explicitOwner || link || element;
    var href = link ? (attribute(link, 'href') || link.href || '') : '';
    var baseUrl = documentRef && documentRef.location
      ? documentRef.location.href
      : 'https://store.invalid';
    var checkoutOwner = closest(
      element,
      '#cart-checkout, #cart-sidebar-checkout, [name="checkout"], [data-oai-checkout]'
    );
    var checkoutHref = attribute(source, 'data-href') || href;
    var isCheckout = Boolean(checkoutOwner) ||
      /(^|\/)checkout(?:[/?#]|$)/i.test(checkoutHref);

    return {
      source: source,
      ignored: Boolean(closest(element, '[data-oai-ignore]')),
      explicitEvent: explicitOwner
        ? attribute(explicitOwner, 'data-oai-event')
        : '',
      href: href,
      isCalendly: isCalendlyUrl(href, baseUrl),
      inNavigation: Boolean(closest(element, 'header, nav, [role="navigation"]')),
      isCheckout: isCheckout,
      inMainContent: Boolean(closest(element, '#MainContent')),
      isPage: String(context.pageType || '').toLowerCase() === 'page',
      pageKind: classifyPage(context, documentRef)
    };
  }

  function contentFromElement(element, documentRef, contentType) {
    var href = attribute(element, 'href') || element.href || '';
    var baseUrl = documentRef && documentRef.location
      ? documentRef.location.href
      : 'https://store.invalid';

    return sanitizeContent({
      id: attribute(element, 'data-oai-content-id') ||
        normalizeDestination(href, baseUrl),
      name: attribute(element, 'data-oai-label') ||
        attribute(element, 'aria-label') ||
        attribute(element, 'title') ||
        element.textContent,
      content_type: attribute(element, 'data-oai-content-type') || contentType
    });
  }

  function createRuntime(windowRef, documentRef, config) {
    var state = {
      allowed: false,
      viewsSent: false
    };

    function queue() {
      if (typeof windowRef.oaiq === 'function') {
        windowRef.oaiq.apply(windowRef, arguments);
      }
    }

    function measure() {
      if (!state.allowed || config.designMode) return;
      queue.apply(null, arguments);
    }

    function emitInitialViews() {
      if (state.viewsSent || !state.allowed || config.designMode) return;

      state.viewsSent = true;
      measure('measure', 'page_viewed', pageViewData(config.context));

      var kind = classifyPage(config.context, documentRef);
      if (
        ['product', 'collection', 'landing_page', 'sale_page']
          .indexOf(kind) !== -1
      ) {
        measure(
          'measure',
          'contents_viewed',
          contentViewData(config.context, kind)
        );
      }
    }

    function setConsent(allowed) {
      state.allowed = Boolean(allowed);
      queue('consent', state.allowed);

      if (state.allowed) emitInitialViews();
    }

    return {
      emitInitialViews: emitInitialViews,
      measure: measure,
      setConsent: setConsent,
      state: state
    };
  }

  function readShopifyConsent(windowRef) {
    var privacy = windowRef.Shopify && windowRef.Shopify.customerPrivacy;

    if (
      !privacy ||
      typeof privacy.analyticsProcessingAllowed !== 'function' ||
      typeof privacy.marketingAllowed !== 'function'
    ) {
      return null;
    }

    try {
      return consentAllowed({
        analytics: privacy.analyticsProcessingAllowed(),
        marketing: privacy.marketingAllowed()
      });
    } catch (error) {
      return null;
    }
  }

  function installConsentListeners(
    windowRef,
    documentRef,
    config,
    runtime
  ) {
    documentRef.addEventListener('visitorConsentCollected', function (event) {
      var detail = event.detail || {};
      runtime.setConsent(consentAllowed({
        analytics: detail.analyticsAllowed,
        marketing: detail.marketingAllowed
      }));
    });

    documentRef.addEventListener('dm:cookie-consent-accepted', function () {
      runtime.setConsent(true);
    });

    function applyAvailableConsent() {
      var allowed = readShopifyConsent(windowRef);
      runtime.setConsent(
        allowed === null
          ? Boolean(config.consentCookieAccepted)
          : allowed
      );
    }

    if (readShopifyConsent(windowRef) !== null) {
      applyAvailableConsent();
      return;
    }

    if (
      windowRef.Shopify &&
      typeof windowRef.Shopify.loadFeatures === 'function'
    ) {
      windowRef.Shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' }
      ], function (error) {
        if (error && config.debug && windowRef.console) {
          windowRef.console.warn('[OpenAI Ads] Customer Privacy API unavailable');
        }
        applyAvailableConsent();
      });
      return;
    }

    applyAvailableConsent();
  }

  function contentTypeForEvent(eventName) {
    if (eventName === 'menu_clicked') return 'navigation';
    if (eventName === 'appointment_cta_clicked') return 'appointment_cta';
    if (eventName === 'sale_cta_clicked') return 'sale_cta';
    if (eventName === 'landing_cta_clicked') return 'landing_cta';
    return 'cta';
  }

  function installInteractionListeners(
    windowRef,
    documentRef,
    config,
    runtime
  ) {
    documentRef.addEventListener('click', function (event) {
      var target = event.target;
      if (target && target.nodeType === 3) target = target.parentElement;
      if (!target || typeof target.closest !== 'function') return;

      var element = target.closest(
        '[data-oai-event], a[href], button, input[type="submit"]'
      );
      if (!element) return;

      var description = describeAction(
        element,
        config.context,
        documentRef
      );
      var eventName = classifyAction(description);

      if (
        !eventName &&
        description.explicitEvent &&
        config.debug &&
        windowRef.console
      ) {
        windowRef.console.warn(
          '[OpenAI Ads] Invalid custom event name:',
          description.explicitEvent
        );
      }

      if (!eventName) return;

      if (eventName === 'checkout_started') {
        runtime.measure(
          'measure',
          'checkout_started',
          checkoutData(config.context)
        );
        return;
      }

      var customArguments = customMeasureArguments(
        eventName,
        contentFromElement(
          description.source,
          documentRef,
          contentTypeForEvent(eventName)
        )
      );
      runtime.measure.apply(null, customArguments);
    });

    windowRef.addEventListener('klaviyoForms', function (event) {
      var eventName = klaviyoEvent(event.detail);
      if (!eventName) return;

      runtime.measure(
        'measure',
        eventName,
        { type: 'customer_action' }
      );
    });

    // The theme's built-in newsletter form (assets/dm-newsletter.js) sends
    // signups to Klaviyo without being a Klaviyo form, so it announces them
    // with its own event.
    documentRef.addEventListener('dm:newsletter:subscribed', function () {
      runtime.measure(
        'measure',
        'lead_created',
        { type: 'customer_action' }
      );
    });
  }

  function isCartAddRequest(url, method, baseUrl) {
    if (!url || String(method || 'GET').toUpperCase() !== 'POST') {
      return false;
    }

    try {
      var pathname = new URL(url, baseUrl || 'https://store.invalid').pathname;
      return /(^|\/)cart\/add(?:\.js)?\/?$/i.test(pathname);
    } catch (error) {
      return false;
    }
  }

  function trackCartResponse(runtime, config, response) {
    var data = cartAddData(response, config.context.currency);
    if (!data) return;
    runtime.measure('measure', 'items_added', data);
  }

  function installFetchObserver(windowRef, config, runtime) {
    if (typeof windowRef.fetch !== 'function') return;

    var originalFetch = windowRef.fetch;
    windowRef.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      var method = (init && init.method) || (input && input.method);
      var result = originalFetch.apply(this, arguments);

      if (
        isCartAddRequest(url, method, windowRef.location && windowRef.location.href)
      ) {
        result.then(function (response) {
          if (!response || !response.ok || typeof response.clone !== 'function') {
            return;
          }

          response.clone().json()
            .then(function (body) {
              trackCartResponse(runtime, config, body);
            })
            .catch(function () {});
        }).catch(function () {});
      }

      return result;
    };
  }

  function installXhrObserver(windowRef, config, runtime) {
    if (!windowRef.XMLHttpRequest || !windowRef.XMLHttpRequest.prototype) {
      return;
    }

    var prototype = windowRef.XMLHttpRequest.prototype;
    var originalOpen = prototype.open;
    var originalSend = prototype.send;

    prototype.open = function (method, url) {
      this.__dmOaiCartAdd = isCartAddRequest(
        url,
        method,
        windowRef.location && windowRef.location.href
      );
      return originalOpen.apply(this, arguments);
    };

    prototype.send = function () {
      if (this.__dmOaiCartAdd) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status > 299) return;

          try {
            var body = typeof this.response === 'object' && this.response
              ? this.response
              : JSON.parse(this.responseText);
            trackCartResponse(runtime, config, body);
          } catch (error) {}
        });
      }

      return originalSend.apply(this, arguments);
    };
  }

  function init(windowRef, documentRef) {
    var config = windowRef.DMOpenAIAdsConfig;

    if (!config || windowRef.__dmOpenAIAdsInitialized) return null;

    windowRef.__dmOpenAIAdsInitialized = true;

    var runtime = createRuntime(windowRef, documentRef, config);
    installConsentListeners(windowRef, documentRef, config, runtime);
    installInteractionListeners(windowRef, documentRef, config, runtime);
    installFetchObserver(windowRef, config, runtime);
    installXhrObserver(windowRef, config, runtime);

    return runtime;
  }

  return {
    STANDARD_EVENTS: STANDARD_EVENTS,
    cartAddData: cartAddData,
    classifyAction: classifyAction,
    classifyPage: classifyPage,
    consentAllowed: consentAllowed,
    contentFromElement: contentFromElement,
    contentViewData: contentViewData,
    createRuntime: createRuntime,
    customMeasureArguments: customMeasureArguments,
    describeAction: describeAction,
    init: init,
    isCalendlyUrl: isCalendlyUrl,
    isCartAddRequest: isCartAddRequest,
    klaviyoEvent: klaviyoEvent,
    pageViewData: pageViewData,
    sanitizeContent: sanitizeContent,
    validCustomEventName: validCustomEventName
  };
});
